// ============================================================
// EVE — CENTRAL CONTENT CONFIGURATION
// ------------------------------------------------------------
// Everything editable lives in this single file.
//  · Replace `null` values with official URLs as they become available.
//  · Official photos: drop files into /public/assets/photos/
//    with the exact file names referenced below (paths are in `photos`).
//    Missing photos render a designed placeholder automatically.
//  · Official documents: drop files into /public/assets/docs/
//    and flip `available` to true on the matching entry in `epk.files`.
//  · The band is EVE. EVECORE is only the fan community name.
// ============================================================

export type Social = { name: string; url: string | null };
export type Platforms = {
  spotify: string | null;
  appleMusic: string | null;
  youtubeMusic: string | null;
};

export type Release = {
  id: string;
  title: string;
  type: "Single" | "Album";
  date: string | null;
  latest: boolean;
  description: string;
  accent: string;
  cover?: string;
  platforms: Platforms;
};

export type UpcomingShow = {
  id: string;
  title: string;
  location: string;
  date: string;
  ticketUrl: string | null;
  photo: string | null;
};

export type EpkFile = {
  id: string;
  label: string;
  path: string;
  available: boolean;
};

export const site = {
  band: {
    name: "EVE",
    title: "EVE | Official Website",
    community: "EVECORE",
    origin: "Indonesia",
    genre: "Modern Rock / Rock Party",
    location: "Surabaya, Indonesia",
    headlineLines: ["THE ULTIMATE", "ROCK PARTY", "EXPERIENCE"],
    subheadline: "Feel the Energy. Break Your Limit.",
    description:
      "EVE is a modern rock band from Indonesia, blending powerful melodies, explosive energy, and an unforgettable live rock party experience.",
    epkIntro:
      "EVE is a modern rock band from Indonesia, blending powerful melodies, explosive energy, and an unforgettable live rock party experience. Every show is built as a party — loud, cinematic, and made to be felt as much as heard.",
  },

  links: {
    // Official EVECORE Universe — Instagram Broadcast Channel (verified)
    universe: "https://www.instagram.com/channel/HQDGbRuHi7OdRqbD/",
  },

  socials: [
    { name: "Instagram", url: "https://www.instagram.com/evecore.official" },
    { name: "YouTube", url: "https://www.youtube.com/@EVECORE.Official" },
    { name: "Spotify", url: "https://open.spotify.com/artist/025gRCBlUWass9WTmy6bnK" },
    { name: "Apple Music", url: "https://music.apple.com/id/artist/eve/1812541060" },
    { name: "TikTok", url: "https://www.tiktok.com/@eve2core.official" },
  ] as Social[],

  contact: {
    managementEmail: "evecoreofficial@gmail.com",
    whatsapp: "6287854444858",
  },

  photos: {
    hero: { path: "/assets/photos/hero-band.webp", alt: "EVE — all five members live on stage, cinematic wide shot" },
    about: { path: "/assets/photos/about-band.webp", alt: "EVE — vocalist and band performing live" },
    epk: { path: "/assets/photos/epk-band.jpg", alt: "EVE — official studio portrait, all five members" },
  },

  releases: [
    {
      id: "the-day-i-found-you",
      title: "The Day I Found You",
      type: "Single",
      date: "October 2, 2026",
      latest: true,
      description: "A song about the moment everything changed when I found you.",
      accent: "#00D9FF",
      cover: "/assets/covers/the-day-i-found-you.jpg",
      platforms: { spotify: "https://open.spotify.com/track/29BLVFKdHJXHjNvgM07sJ5", appleMusic: "https://music.apple.com/us/song/the-day-i-found-you/6815329245", youtubeMusic: "https://music.youtube.com/watch?v=okac8Ey4vBA" },
    },
    {
      id: "pretty",
      title: "Pretty",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#FF2D9A",
      cover: "/assets/covers/pretty.jpg",
      platforms: { spotify: "https://open.spotify.com/track/0FGeRY2RW26HcAG08TBabR", appleMusic: "https://music.apple.com/us/song/pretty/1866402590", youtubeMusic: "https://music.youtube.com/watch?v=__cYG04WtRg" },
    },
    {
      id: "limit",
      title: "Limit",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#E60039",
      cover: "/assets/covers/limit.jpg",
      platforms: { spotify: "https://open.spotify.com/track/7eD36zs7F8u6jFhnqTUpZC", appleMusic: "https://music.apple.com/us/song/limit/6771899673", youtubeMusic: "https://music.youtube.com/watch?v=0kQ5kAyslz0" },
    },
    {
      id: "mama",
      title: "Mama",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#7B2CFF",
      cover: "/assets/covers/mama.jpg",
      platforms: { spotify: "https://open.spotify.com/track/5Dh0UQfGRwwAFxsrIH2m70", appleMusic: "https://music.apple.com/us/song/mama/1852983959", youtubeMusic: "https://music.youtube.com/watch?v=5CIzi1Z9M1g" },
    },
    {
      id: "destiny",
      title: "Destiny",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#00D9FF",
      cover: "/assets/covers/destiny.jpg",
      platforms: { spotify: "https://open.spotify.com/track/1RKLwonzKHvhfbhAtJgrUA", appleMusic: "https://music.apple.com/us/song/destiny/1826466914", youtubeMusic: "https://music.youtube.com/watch?v=Lt0VfAUnux8" },
    },
    {
      id: "pulang-kembali",
      title: "Pulang Kembali",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#FF2D9A",
      cover: "/assets/covers/pulang-kembali.jpg",
      platforms: { spotify: "https://open.spotify.com/track/1QHZfoz3n1IgNt91z1a7Zt", appleMusic: "https://music.apple.com/us/song/pulang-kembali/1796101527", youtubeMusic: "https://music.youtube.com/watch?v=MAS6WNV0RYw" },
    },
    {
      id: "next-to-you",
      title: "Next to You",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#E60039",
      cover: "/assets/covers/next-to-you.jpg",
      platforms: { spotify: "https://open.spotify.com/track/4b6eaNoHMYrTmB2h3kxxmO", appleMusic: "https://music.apple.com/us/song/next-to-you/1878195568", youtubeMusic: "https://music.youtube.com/watch?v=kWWnI3WCbCI" },
    },
    {
      id: "running-through-the-dark",
      title: "Running Through the Dark",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#7B2CFF",
      cover: "/assets/covers/running-through-the-dark.jpg",
      platforms: { spotify: "https://open.spotify.com/track/1UCRwexwzc90rjlb51PymF", appleMusic: "https://music.apple.com/us/song/running-through-the-dark/1847628159", youtubeMusic: "https://music.youtube.com/watch?v=kIVWwYWiGI8" },
    },
    {
      id: "mama-acoustic",
      title: "Mama (Acoustic Version)",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#00D9FF",
      cover: "/assets/covers/mama-acoustic.jpg",
      platforms: { spotify: "https://open.spotify.com/track/2CCzZtcVPODp3VPBQD5ZCX", appleMusic: "https://music.apple.com/us/song/mama-acoustic-version/1858233121", youtubeMusic: "https://music.youtube.com/watch?v=jeH63f-JFBs" },
    },
    {
      id: "garuda",
      title: "Garuda",
      type: "Single",
      date: null,
      latest: false,
      description: "",
      accent: "#E60039",
      cover: "/assets/covers/garuda.jpg",
      platforms: { spotify: "https://open.spotify.com/track/3YxRYHU3sVEI4sfK2f1Gui", appleMusic: "https://music.apple.com/us/song/garuda/1814225347", youtubeMusic: "https://music.youtube.com/watch?v=mOIzUEaNGqY" },
    },
  ] as Release[],

  featuredReleaseId: "the-day-i-found-you",

  videos: {
    featured: {
      title: "Pretty — Live Performance",
      youtubeId: "n78sPntIZvM", // Official EVE channel link supplied
      youtubeUrl: "https://youtu.be/n78sPntIZvM", // full watch URL for the CTA button
    },
    // Only add real, verified videos. The gallery renders nothing when empty.
    gallery: [
      { id: "destiny", title: "Destiny — Official Music Video", youtubeId: "A4-QJkt39G4", url: "https://youtu.be/A4-QJkt39G4" },
      { id: "mama", title: "Mama — Official Video (Extended Version)", youtubeId: "ovrFvFayyyY", url: "https://youtu.be/ovrFvFayyyY" },
      { id: "pulang-kembali", title: "Pulang Kembali — Official Video", youtubeId: "4ZAwh1aP_tw", url: "https://youtu.be/4ZAwh1aP_tw" },
      { id: "running-through-the-dark", title: "Running Through the Dark — Official Music Video", youtubeId: "m6U1Bm9CTM8", url: "https://youtu.be/m6U1Bm9CTM8" },
      { id: "limit-live", title: "Limit — Rock in Surabaya Showcase 2026", youtubeId: "XuMTFNymNeY", url: "https://youtu.be/XuMTFNymNeY" },
    ],
  },

  shows: {
    upcoming: [
      { id: "show-2026-11-22", title: "To Be Announced", location: "TBA", date: "November 22, 2026", ticketUrl: null, photo: null },
      { id: "show-2026-11-28", title: "To Be Announced", location: "TBA", date: "November 28, 2026", ticketUrl: null, photo: null },
      { id: "show-2026-12-20", title: "To Be Announced", location: "TBA", date: "December 20, 2026", ticketUrl: null, photo: null },
    ] as UpcomingShow[],
    emptyStateTitle: "NEW SHOWS COMING SOON.",
    emptyStateSub: "STAY TUNED.",
    // Live photo gallery — pure imagery, no event/date labels (per band's request).
    gallery: [
      { src: "/assets/photos/live-1.webp", alt: "EVE live — Dialog Cinta, Jombang" },
      { src: "/assets/photos/live-2.webp", alt: "EVE live — Indomie Nyemek on the Block, Surabaya" },
      { src: "/assets/photos/live-3.webp", alt: "EVE live — Rock in Surabaya, Mojokerto" },
      { src: "/assets/photos/live-4.webp", alt: "EVE live — Dream Desire Festival, Pandaan" },
      { src: "/assets/photos/live-5.webp", alt: "EVE live — Scooter Brotherhood 24th Anniversary, Bali" },
      { src: "/assets/photos/show-rock-surabaya.jpg", alt: "EVE on stage — live performance" },
    ],
  },

  epk: {
    files: [
      { id: "epk-pdf", label: "EPK — Electronic Press Kit (PDF)", path: "/assets/docs/eve-epk.pdf", available: true },
      { id: "technical-rider", label: "Technical Rider (PDF)", path: "/assets/docs/eve-technical-rider.pdf", available: true },
      { id: "press-photos", label: "Official Press Photos (ZIP)", path: "/assets/docs/eve-press-photos.zip", available: false },
    ] as EpkFile[],
    featuredPerformance: {
      title: "Pretty — Live Performance",
      path: "/video",
    },
  },

  nav: [
    { label: "HOME", path: "/" },
    { label: "ABOUT", path: "/about" },
    { label: "MUSIC", path: "/music" },
    { label: "VIDEO", path: "/video" },
    { label: "SHOWS", path: "/shows" },
    { label: "EPK", path: "/epk" },
    { label: "CONTACT", path: "/contact" },
  ],
};
